import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import ProfilePicture from "../../profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const PlayerJoinRequestsTab = ({ teamId, onRequestProcessed }) => {
  const { t } = useTranslation();
  const [joinRequests, setJoinRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchJoinRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error(t("errors.noAuthToken"));
      }

      const response = await fetch(`${API_URL}/team/get-all-players-requests-to-join-team?teamId=${teamId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t("teams.failedToFetchJoinRequests"));
      }

      const data = await response.json();
      console.log("API response data:", data);
      // Convert players object to array, or use requests array if players is empty
      let playersArray = [];
      if (data.data && data.data.players) {
        playersArray = Object.values(data.data.players || {});
      } else if (data.data && data.data.requests) {
        // Fallback to requests array with player data
        playersArray = data.data.requests.map(request => ({
          ...request.player,
          requestId: request._id,
          requestCreatedAt: request.createdAt,
          createdAt: request.createdAt
        }));
      }
      console.log("Processed players array:", playersArray);
      setJoinRequests(playersArray);
    } catch (err) {
      console.error("Error fetching join requests:", err);
      setError(err.message || t("teams.failedToLoadJoinRequests"));
    } finally {
      setIsLoading(false);
    }
  }, [teamId, t]);

  useEffect(() => {
    fetchJoinRequests();
  }, [fetchJoinRequests]);

  const handleRequest = async (requestId, status) => {
    try {
      console.log('Starting request action:', { requestId, status });
      setActionLoading(requestId);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error(t("errors.noAuthToken"));
      }

      console.log('Before API call');
      const response = await fetch(`${API_URL}/team/approve-or-reject-player-request-to-join-team`, {
        method: 'PUT',
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          requestId,
          teamId,
          status,
        }),
      });

      console.log('API response received:', response.status);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t("teams.failedToProcessRequest", { action: status }));
      }

      console.log('Before state update');
      setJoinRequests(prevRequests => {
        console.log('Inside state update callback, previous requests:', prevRequests);
        const newRequests = prevRequests.filter(request => request.requestId !== requestId);
        console.log('Filtered requests:', newRequests);
        return newRequests;
      });
      
      // Notify parent component to refresh team data
      if (onRequestProcessed) {
        onRequestProcessed();
      }
      
      // Show success message
      setSuccessMessage(t("teams.requestProcessedSuccess", { action: status }));
      setTimeout(() => setSuccessMessage(null), 4000);
      
      console.log('After state update');
      setActionLoading(null);
    } catch (err) {
      console.error(`Error ${status}ing request:`, err);
      setError(err.message || t("teams.failedToProcessRequest", { action: status }));
      setActionLoading(null);
    }
  };

  const RequestCard = ({ user }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <ProfilePicture
                image={user?.profilePicture}
                alt={user?.userName}
                className="h-14 w-14 rounded-xl object-cover ring-2 ring-gray-100"
                myUser={user}
              />
            </div>
            <div className="">
              <h3 className="text-lg font-semibold text-gray-900">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-sm text-gray-500">
                {user?.userName || t("teams.unknownUser")}
              </p>
              <p className="text-sm text-gray-500">
                {t("teams.requested")} {new Date(user.requestCreatedAt || user.createdAt).toLocaleDateString()}
              </p>
              {user.message && (
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg mt-2">
                  "{user.message}"
                </p>
              )}
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => handleRequest(user.requestId, "approved")}
              disabled={actionLoading === user.requestId}
              className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {actionLoading === user.requestId ? (
                <div className="animate-spin h-5 w-5 border-2 border-emerald-600 border-t-transparent rounded-full"></div>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t("teams.approve")}
                </>
              )}
            </button>
            <button
              onClick={() => handleRequest(user.requestId, "rejected")}
              disabled={actionLoading === user.requestId}
              className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {actionLoading === user.requestId ? (
                <div className="animate-spin h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full"></div>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  {t("teams.reject")}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div
        role="tabpanel"
        id="requests-panel"
        aria-labelledby="requests-tab"
        className="mt-8"
      >
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#F15213]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="tabpanel"
        id="requests-panel"
        aria-labelledby="requests-tab"
        className="mt-8"
      >
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          <p className="font-medium">{error}</p>
          <button
            className="mt-3 text-sm font-medium text-red-600 hover:text-red-700 underline"
            onClick={fetchJoinRequests}
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  console.log("Rendering with joinRequests:", joinRequests);

  return (
    <div
      role="tabpanel"
      id="requests-panel"
      aria-labelledby="requests-tab"
      className="mt-8"
    >
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t("teams.joinRequests")}</h2>
          <p className="text-gray-500 mt-1">
            {t("teams.managePlayerRequests")}
          </p>
        </div>
        <div className="px-4 py-2 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
          {joinRequests.length} {t("common.pending")}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 text-green-700">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        </div>
      )}

      {joinRequests.length > 0 ? (
        <div className="space-y-4">
          {joinRequests.map((user) => (
            <RequestCard key={user._id} user={user} />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <div className="max-w-sm mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t("teams.noPendingRequests")}
            </h3>
            <p className="text-gray-500">
              {t("teams.requestsWillAppearHere")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerJoinRequestsTab;
